import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePipelineStore } from 'dashboard/stores/pipeline';

// Sante-CRM: lógica do quadro Kanban do funil odontológico.
// Agrupa os cards por estágio e trata o drag & drop do vuedraggable.
export function usePipelineBoard() {
  const { t } = useI18n();
  const store = usePipelineStore();
  const { pipelines, activePipelineId, uiFlags } = storeToRefs(store);

  const activePipeline = computed(() => store.activePipeline);
  const isLoading = computed(
    () => uiFlags.value.isFetching || uiFlags.value.isFetchingItems
  );

  // Colunas do quadro: cada estágio com seus cards ordenados por posição.
  const columns = computed(() =>
    store.stages.map(stage => ({
      stage,
      items: store.itemsByStageId(stage.id),
    }))
  );

  const fetchBoard = async () => {
    try {
      await store.fetchPipelines();
      await store.fetchItems();
    } catch (error) {
      useAlert(t('PIPELINE.API.FETCH_ERROR'));
    }
  };

  const switchPipeline = async pipelineId => {
    try {
      await store.setActivePipeline(pipelineId);
    } catch (error) {
      useAlert(t('PIPELINE.API.FETCH_ERROR'));
    }
  };

  // Handler do evento @change do vuedraggable de cada coluna.
  // `added` = card vindo de outra coluna; `moved` = reordenado na mesma.
  const onColumnChange = async (stageId, event) => {
    const change = event.added || event.moved;
    if (!change) return;

    try {
      await store.moveItem({
        itemId: change.element.id,
        stageId,
        position: change.newIndex,
      });
    } catch (error) {
      useAlert(t('PIPELINE.API.MOVE_ERROR'));
    }
  };

  // Cria um card manualmente (modal do quadro); entra no fim da coluna.
  // Retorna true/false para a view decidir se fecha o modal.
  const createCard = async attributes => {
    try {
      await store.createItem({
        ...attributes,
        position: store.itemsByStageId(attributes.pipeline_stage_id).length,
      });
      useAlert(t('PIPELINE.ADD_CARD.SUCCESS_MESSAGE'));
      return true;
    } catch (error) {
      useAlert(t('PIPELINE.ADD_CARD.ERROR_MESSAGE'));
      return false;
    }
  };

  onMounted(fetchBoard);

  return {
    pipelines,
    activePipeline,
    activePipelineId,
    columns,
    isLoading,
    fetchBoard,
    switchPipeline,
    onColumnChange,
    createCard,
  };
}
